import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from 'app/hooks';
import { getStripeSubscriptionInfo } from 'app/basicInformationSlice/basicInformationSlice';
import { getAccessToken } from 'utils/sessionStorage';
import {
  completeCamInboundOAuth,
  hasPendingCamOAuthRedirect,
  isCamOAuthLoginRoute,
  isCamUpgradeModalPending,
  isOnCamBillingRoute,
  isCamTrialUpgradeFlow,
  notifyCamUpgradeRequired,
} from 'utils/camAuth';

/**
 * PATH 1 helper: complete CAM inbound OAuth when oauth_redirect is present and GM session exists
 * (e.g. already logged in, or PATH 2 handoff seeded session). Not used for normal GM dashboard login.
 */
const CamOAuthRedirectHandler = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Login page owns PATH 2 bridge (handoff + oauth_redirect on same URL).
  const loginPageOwnsOAuth =
    isCamOAuthLoginRoute(location.pathname) && hasPendingCamOAuthRedirect();
  const [isOauthRedirecting, setIsOauthRedirecting] = React.useState(false);
  const oauthCompletionStartedRef = React.useRef(false);

  const tryCompleteOAuth = React.useCallback(() => {
    if (loginPageOwnsOAuth || isCamUpgradeModalPending()) {
      return;
    }
    const onBillingRoute =
      isOnCamBillingRoute() ||
      location.pathname.includes('/upgrade-plan') ||
      location.pathname.includes('/payment') ||
      location.pathname.includes('/success');
    if (onBillingRoute && isCamTrialUpgradeFlow()) {
      return;
    }
    if (oauthCompletionStartedRef.current) {
      return;
    }
    if (!getAccessToken() || !hasPendingCamOAuthRedirect()) {
      return;
    }
    oauthCompletionStartedRef.current = true;
    void completeCamInboundOAuth({
      dispatch,
      getStripeSubscriptionInfo,
      onUpgradeRequired: () => notifyCamUpgradeRequired(),
      onRedirecting: () => setIsOauthRedirecting(true),
    }).finally(() => {
      if (!hasPendingCamOAuthRedirect() || isCamUpgradeModalPending()) {
        oauthCompletionStartedRef.current = !hasPendingCamOAuthRedirect();
      }
    });
  }, [dispatch, loginPageOwnsOAuth, location.pathname]);

  React.useEffect(() => {
    tryCompleteOAuth();
  }, [tryCompleteOAuth]);

  if (loginPageOwnsOAuth) {
    return null;
  }

  return (
    <>
      {isOauthRedirecting && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: 60, height: 60, fontSize: 32 }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <div style={{ marginTop: 16, fontWeight: 500, fontSize: 18 }}>
              Redirecting to CAM, please wait...
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CamOAuthRedirectHandler;
